import { contentTopicRequest } from "../../questionBank/qbankNavigation";
import { EXPLORE_ROUTES } from "../exploreRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import HomeDashboardLayout from "../HomeDashboardLayout";
import HeaderProfileSection from "../HeaderProfileSection";
import LearningLanesCard from "./LearningLanesCard";
import FlashCardQuickAction from "./FlashCardQuickAction";
import OverViewCard from "./OverViewCard";
import { FlashCardStartShimmer } from "./FlashCardShimmer";
import { FLASH_CARD_ROUTES } from "./flashCardRoutes";
import { useSidebarData } from "../../hooks/useSidebarData";
import {
  useFlashCardActions,
  useFlashCardProgress,
} from "../../hooks/useFlashCards";
import {
  buildQuestionBankFlowState,
  getStoredQuestionBankFlowState,
  persistQuestionBankFlowState,
} from "../../questionBank/qbankFlowState";
import { resolveQuestionBankStateId } from "../../questionBank/stateUtils";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../DashboardQuestionBank.css";
import "./StartFlashCardd.css";

const FLASH_CARDS_CATEGORY_ID = 9;
const FLASH_CARD_API_CATEGORY_ID = 9;

const StartFlashCard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: sidebarData = null } = useSidebarData();
  const flowState = useMemo(
    () =>
      buildQuestionBankFlowState(
        location.state,
        getStoredQuestionBankFlowState(),
      ),
    [location.state],
  );
  const stateId =
    resolveQuestionBankStateId({ locationState: flowState, sidebarData }) || 13;
  const subjectId = Number(
    flowState?.selectedSubject?.id || flowState?.subject_id || 0,
  );
  const [language, setLanguage] = useState(flowState?.language || "EN");
  const params = useMemo(
    () => ({
      states_id: stateId,
      category_id: FLASH_CARD_API_CATEGORY_ID,
      subject_id: subjectId,
      language,
      ...contentTopicRequest(flowState),
    }),
    [flowState, language, stateId, subjectId],
  );
  const { data, isLoading, isError, error, refetch } = useFlashCardProgress(
    params,
    Boolean(subjectId),
  );
  const { reset } = useFlashCardActions();
  const summary = data?.payload?.summary || {};
  const lastViewed = data?.payload?.last_viewed || {};
  const progress = Math.min(100, Number(summary.progress_percent || 0));
  const progressLabel = progress;

  useEffect(() => {
    persistQuestionBankFlowState(flowState);
  }, [flowState]);

  const openDeck = (cardProgress = "") => {
    const nextState = buildQuestionBankFlowState({
      ...flowState,
      category_id: FLASH_CARDS_CATEGORY_ID,
      language,
      card_progress: cardProgress,
      qbankBackTarget: {
        pathname: FLASH_CARD_ROUTES.start,
        state: { ...flowState, language },
      },
    });
    persistQuestionBankFlowState(nextState);
    navigate(
      cardProgress === "remaining_card"
        ? FLASH_CARD_ROUTES.study
        : FLASH_CARD_ROUTES.list,
      { state: nextState },
    );
  };
  const goBack = () =>
    flowState?.qbankBackTarget?.pathname
      ? navigate(flowState.qbankBackTarget.pathname, {
          state: flowState.qbankBackTarget.state,
        })
      : navigate(EXPLORE_ROUTES.subjects, { state: flowState });
  const handleReset = async () => {
    if (!summary.reset_status) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Reset Flash Cards?",
      text:
        data?.payload?.reset_message ||
        "This will clear your Flash Card learning progress.",
      showCancelButton: true,
      confirmButtonText: "Reset",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#5a22ea",
    });
    if (!result.isConfirmed) return;

    try {
      await reset.mutateAsync(params);
      await refetch();
      await Swal.fire({
        icon: "success",
        title: "Progress reset",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (requestError) {
      await Swal.fire({
        icon: "error",
        title: "Reset failed",
        text: requestError?.response?.data?.message || "Please try again.",
      });
    }
  };

  return (
    <>
      <style>{`.homeDashboard-wrapper .page-content { padding-right: 0; }`}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page podcastListt">
          <div className="qbl-main" aria-label="Flash card content">
            <section className="qbl-panel">
              <div className="right-notify d-flex justify-content-between align-items-center">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={goBack}
                    aria-label="Go back"
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                </div>
                 <h4 className="d-flex categories-page-title mb-0">
                    Flash Cards
                  </h4>
                <div className="d-flex align-items-center gap-2">
                 
                  <label className="flash-language-select">
                    <i className="bi bi-translate" aria-hidden="true" />
                    <span>Language</span>
                    <select
                      value={language}
                      onChange={(event) => setLanguage(event.target.value)}
                      aria-label="Flash Card language"
                    >
                      <option value="EN">English</option>
                      <option value="HI">Hindi</option>
                      <option value="BOTH">Both</option>
                    </select>
                    <i
                      className="bi bi-chevron-down flash-language-select__chevron"
                      aria-hidden="true"
                    />
                  </label>
                </div>
                <HeaderProfileSection />
              </div>
              {isLoading ? <FlashCardStartShimmer /> : null}
              {isError ? (
                <div className="choose-state__status choose-state__status--error">
                  {error?.response?.data?.message ||
                    "Unable to load Flash Card progress."}
                </div>
              ) : null}
              {!isLoading && !isError ? (
                <>
                  <div className="qb-body">
                    <div className="d-flex align-items-center justify-content-center flashStart">
                      <div className="qb-copy">
                        <div className="qb-loc">
                          <span className="flash-title">
                            Keep the streak alive
                          </span>
                        </div>
                        <h1 className="qb-title">
                          Start Learning <span>FlashCard</span>
                        </h1>
                        <p className="qb-sub">
                          {Number(summary.total_terms || 0)} cards in this deck.{" "}
                          {Number(summary.know || 0)} mastered,{" "}
                          {Number(summary.still_learning || 0)} still learning,
                          and {Number(summary.remaining || 0)} left to explore.
                        </p>
                        <div className="continue-action-wrapper">
                          <button
                            type="button"
                            className="continue-action-button"
                            onClick={() => openDeck("remaining_card")}
                            disabled={!summary.remaining}
                          >
                            <span className="continue-action-play">
                              <i className="bi bi-play-fill" />
                            </span>
                            <span className="continue-action-text">
                              Continue
                            </span>
                          </button>
                          <button
                            type="button"
                            className="continue-action-star"
                            onClick={() => openDeck("")}
                            aria-label="Open all cards"
                          >
                            <i className="bi bi-layers-fill" />
                          </button>
                          <button
                            type="button"
                            className="continue-action-star"
                            onClick={() => openDeck("bookmarked_card")}
                            aria-label="Open bookmarked cards"
                          >
                            <i className="bi bi-star-fill" />
                          </button>
                        </div>
                      </div>
                      <div className="gamification-visual visual-progress">
                        <div className="progress-circle-wrapper">
                          <div
                            className="progress-circle"
                            style={{ "--progress": `${progress * 3.6}deg` }}
                          >
                            <div className="progress-circle__inner">
                              <span className="progress-circle__value">
                                {progressLabel}%
                              </span>
                              <span className="progress-circle__label">
                                progress
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <LearningLanesCard summary={summary} onSelect={openDeck} />
                  <OverViewCard summary={summary} lastViewed={lastViewed} />
                  <FlashCardQuickAction
                    summary={summary}
                    lastViewed={lastViewed}
                    onContinue={() => openDeck("remaining_card")}
                    onReset={handleReset}
                    resetPending={reset.isPending}
                  />
                </>
              ) : null}
            </section>
          </div>
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default StartFlashCard;
